import { Autocomplete, TextField, Tooltip } from "@mui/material";
import AIHighlightWrapper from "components/common/AIHighlightWrapper";
import { useMemo, useState, useEffect } from "react";
import { useGetAvailableIngestionDataQuery } from "services/aiAgents";
import * as staticDataService from "services/staticDataService";
import {
  recordFieldValue,
  recordSuggestionSelection,
} from "utils/aiSuggestionUsageTracker";
import { getSuggestionsConfig } from "utils/aiSuggestionsConfig";
import { getSuggestionsForField as getFieldSuggestions } from "utils/ingestionSuggestions";

const getDisplayValue = (val) => {
  if (val == null) return "";
  if (typeof val === "object" && val.code != null && val.title) {
    return `(${val.code}) ${val.title}`;
  }
  return String(val);
};

/**
 * AISuggestionTextField - Text field with AI-powered suggestions from document ingestion.
 *
 * This component provides autocomplete functionality based on AI-extracted data from
 * uploaded documents. It integrates with the submission's AI ingestion data.
 *
 * Suggestions are shown based on configuration:
 * - If SHOW_ALL_OPTIONS is true: show immediately on focus
 * - If SHOW_ALL_OPTIONS is false: require MIN_CHARACTERS_FOR_SUGGESTIONS characters typed
 *
 * @param {Object} props - Component props
 * @param {string} props.fieldKey - Key for the field to get suggestions for (e.g., 'insured', 'riskDescription')
 * @param {string} props.value - Current value
 * @param {Function} props.onChange - Change handler (receives new value string)
 * @param {string} props.label - Field label
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {number} props.minChars - Override minimum characters required (optional)
 * @param {Object} ...otherProps - Other props passed to TextField
 */
const AISuggestionTextField = ({
  fieldKey,
  value,
  onChange,
  label,
  disabled = false,
  minChars,
  ...otherProps
}) => {
  const suggestionsConfigQuery = staticDataService.useConfigQuery(
    "submission_ai_ingestion_defaults"
  );
  const suggestionsConfig = useMemo(
    () => getSuggestionsConfig(suggestionsConfigQuery.data),
    [suggestionsConfigQuery.data]
  );
  const { data } = useGetAvailableIngestionDataQuery(undefined, {
    skip: !suggestionsConfig.ENABLED,
  });

  const [inputValue, setInputValue] = useState(value || "");

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  if (!suggestionsConfig.ENABLED) {
    return (
      <AIHighlightWrapper fieldKey={fieldKey}>
        <TextField
          type={"text"}
          InputLabelProps={{ shrink: true }}
          InputProps={{ spellCheck: "false" }}
          label={label}
          autoComplete={"off"}
          variant={"filled"}
          onChange={(e) => onChange(e.target.value)}
          value={value || ""}
          fullWidth
          disabled={disabled}
          {...otherProps}
        />
      </AIHighlightWrapper>
    );
  }

  const ingestionData =
    data?.success && data?.results ? { results: data.results } : null;

  const getSuggestionsForField = (key) => {
    return getFieldSuggestions(key, ingestionData);
  };

  const allSuggestions = getSuggestionsForField(fieldKey);
  const resolvedMinChars =
    minChars ?? suggestionsConfig.MIN_CHARACTERS_FOR_SUGGESTIONS;

  // Filter suggestions by confidence threshold (keep all for matching)
  const suggestions = allSuggestions
    .filter((s) => s.confidence >= suggestionsConfig.MIN_CONFIDENCE_THRESHOLD)
    .sort((a, b) => (a?.value || "").localeCompare(b?.value || ""));

  // If no suggestions available, render regular TextField
  if (suggestions.length === 0) {
    return (
      <AIHighlightWrapper fieldKey={fieldKey}>
        <TextField
          type={"text"}
          InputLabelProps={{ shrink: true }}
          InputProps={{ spellCheck: "false" }}
          label={label}
          autoComplete={"off"}
          variant={"filled"}
          onChange={(e) => onChange(e.target.value)}
          value={value || ""}
          fullWidth
          disabled={disabled}
          {...otherProps}
        />
      </AIHighlightWrapper>
    );
  }

  // Check if we should show suggestions
  const shouldShowSuggestions = () => {
    // Don't show if disabled
    if (disabled) return false;

    // Don't show if no suggestions available
    if (suggestions.length === 0) return false;

    if (!suggestionsConfig.SHOW_ALL_OPTIONS) {
      // Check minimum character requirement
      const currentValue = inputValue || "";
      if (currentValue.length < resolvedMinChars) {
        return false;
      }
    }

    return true;
  };

  // Build options: match against input first, then limit display count
  const getOptions = () => {
    if (!shouldShowSuggestions()) return [];
    const allOptions = suggestions.map((s) => s.value);
    const query = (inputValue || "").toLowerCase();
    // If user has typed something, filter all suggestions by input first
    if (query.length > 0) {
      const matched = allOptions.filter((opt) =>
        getDisplayValue(opt).toLowerCase().includes(query)
      );
      return matched.slice(0, suggestionsConfig.MAX_SUGGESTIONS_DISPLAYED);
    }
    return allOptions.slice(0, suggestionsConfig.MAX_SUGGESTIONS_DISPLAYED);
  };

  const options = getOptions();

  // Create a map for quick lookup of suggestion metadata
  const suggestionMap = {};
  suggestions.forEach((s) => {
    suggestionMap[s.value] = s;
  });

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    onChange(newValue);
    recordFieldValue(fieldKey, newValue);
  };

  return (
    <AIHighlightWrapper fieldKey={fieldKey}>
      <Autocomplete
        freeSolo
        sx={{ width: "100%" }}
        options={options}
        value={value || ""}
        inputValue={inputValue}
        onChange={(_, newValue) => {
          handleInputChange(newValue || "");
          if (suggestionMap[newValue]) {
            recordSuggestionSelection(
              fieldKey,
              newValue,
              suggestionMap[newValue]?.fileId,
              suggestionMap[newValue]?.runId
            );
          }
        }}
        onInputChange={(_, newInputValue, reason) => {
          // Only set hasUserTyped if the change was from user input
          if (reason === "input") {
            handleInputChange(newInputValue || "");
          } else if (reason === "clear") {
            setInputValue("");
            onChange("");
            recordFieldValue(fieldKey, "");
          }
        }}
        disabled={disabled}
        // Don't open dropdown on focus, only when typing
        openOnFocus={false}
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            type={"text"}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              ...params.InputProps,
              spellCheck: "false",
            }}
            label={label}
            variant={"filled"}
            fullWidth
            {...otherProps}
          />
        )}
        renderOption={(props, option) => {
          const suggestion = suggestionMap[option];
          const sourceText = suggestion?.sources
            ?.map((s) => `${s.filename} (p.${s.page_number})`)
            .join(", ");

          const tooltipContent = suggestionsConfig.SHOW_SOURCE_INFO ? (
            <>
              <div>
                <strong>{"Source:"}</strong>
                {` ${sourceText || "Unknown"}`}
              </div>
              <div>
                <strong>{"Confidence:"}</strong>
                {` ${((suggestion?.confidence ?? 0) * 100).toFixed(0)}%`}
              </div>
            </>
          ) : null;

          // Highlight matching text
          const matchString = inputValue || "";
          const parts = matchString
            ? option.split(
                new RegExp(
                  `(${matchString.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
                  "gi"
                )
              )
            : [option];

          return (
            <li {...props} key={option}>
              <Tooltip
                title={tooltipContent || ""}
                placement={"right"}
                arrow
                disableHoverListener={!suggestionsConfig.SHOW_SOURCE_INFO}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    gap: "8px",
                  }}
                >
                  <span style={{ flex: 1 }}>
                    {parts.map((part, index) =>
                      part.toLowerCase() === matchString.toLowerCase() ? (
                        <span
                          key={index}
                          style={{
                            color: "#1b5e20",
                            backgroundColor: "#c8e6c9",
                            fontWeight: "bold",
                          }}
                        >
                          {part}
                        </span>
                      ) : (
                        part
                      )
                    )}
                  </span>
                </div>
              </Tooltip>
            </li>
          );
        }}
      />
    </AIHighlightWrapper>
  );
};

export default AISuggestionTextField;
